export type MobilePlatform = 'ios' | 'android' | 'desktop';

export type MobileAppOpenConfig = {
  /** Full custom-scheme URL, e.g. `com.anihis.app://pet-gallery/share/abc`. */
  iosDeepLink: string;
  /** Path segment for Android intent host, e.g. `pet-gallery/share/abc`. */
  androidIntentPath: string;
  androidPackage: string;
  customScheme: string;
  appStoreUrl: string;
  playStoreUrl: string;
  /** Called when redirect to the platform store begins (app not opened in time). */
  onStoreRedirect?: () => void;
};

const STORE_FALLBACK_MS = 2200;

/** iOS/iPadOS vs Android vs desktop (incl. iPad desktop UA). */
export function detectMobilePlatform(): MobilePlatform {
  if (typeof navigator === 'undefined') {
    return 'desktop';
  }
  const ua = navigator.userAgent.toLowerCase();
  const isIosUa = /iphone|ipad|ipod/.test(ua);
  const isIpadOs =
    /macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
  if (isIosUa || isIpadOs) {
    return 'ios';
  }
  if (/android/.test(ua)) {
    return 'android';
  }
  return 'desktop';
}

export function storeUrlForPlatform(
  platform: MobilePlatform,
  config: Pick<MobileAppOpenConfig, 'appStoreUrl' | 'playStoreUrl'>,
): string {
  return platform === 'ios' ? config.appStoreUrl : config.playStoreUrl;
}

/**
 * Tries to open the native app, then redirects to the correct store if the page stays visible.
 */
export function tryOpenMobileAppWithStoreFallback(
  config: MobileAppOpenConfig,
): MobilePlatform {
  const platform = detectMobilePlatform();
  if (platform === 'desktop') {
    return platform;
  }

  let leftPage = false;
  const markLeft = () => {
    leftPage = true;
  };
  const onVisibility = () => {
    if (document.hidden) {
      markLeft();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', markLeft);
  window.addEventListener('blur', markLeft);

  if (platform === 'android') {
    const fallback = encodeURIComponent(config.playStoreUrl);
    const intentUri = `intent://${config.androidIntentPath}#Intent;scheme=${config.customScheme};package=${config.androidPackage};S.browser_fallback_url=${fallback};end`;
    window.location.href = intentUri;
  } else {
    openIosCustomScheme(config.iosDeepLink);
  }

  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', markLeft);
    window.removeEventListener('blur', markLeft);

    if (leftPage || document.hidden) {
      return;
    }

    config.onStoreRedirect?.();
    const storeUrl = storeUrlForPlatform(platform, config);
    window.location.replace(storeUrl);
  }, STORE_FALLBACK_MS);

  return platform;
}

function openIosCustomScheme(deepLink: string): void {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.src = deepLink;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    iframe.remove();
  }, 1500);
  window.location.href = deepLink;
}
